"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useSession } from "next-auth/react";
import { AnimatePresence, motion } from "motion/react";
import { useLocale, useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { useMessengerNotifications } from "@/contexts/MessengerNotificationsContext";

/**
 * Bulle « Marie vous encourage », en bas à droite.
 *
 * Deux arrivées : en direct quand la personne est en ligne (événement
 * cheerReceived du canal personnel), ou à la visite suivante (les
 * encouragements pas encore vus sont relus au chargement). Plusieurs
 * encouragements en attente tiennent dans une seule bulle.
 *
 * La cloche barrée met les encouragements en pause (1 heure, 1 journée,
 * 1 mois) ; la coupure définitive se règle dans les préférences du compte.
 */

// La bulle se referme seule : elle ne doit pas rester posée sur une vidéo.
// Le décompte s'arrête tant que la souris est dessus.
const AUTO_CLOSE_MS = 12000;
// Écrans où rien ne doit venir distraire.
const QUIET_PATHS = ["/onboarding", "/call/", "/appel/"];
const SNOOZES = ["hour", "day", "month"];

const rankIcon = (level) => `/static/icons/leagues/${level || 1}.svg`;

function Face({ person, size = 44 }) {
    const [broken, setBroken] = useState(false);
    const style = { width: size, height: size };
    if (person?.photo && !broken) {
        return (
            <Image
                src={person.photo}
                alt=""
                width={size}
                height={size}
                style={style}
                className="flex-shrink-0 rounded-full object-cover ring-2 ring-white dark:ring-bgDark-800"
                onError={() => setBroken(true)}
            />
        );
    }
    return (
        <span
            style={style}
            className="flex flex-shrink-0 items-center justify-center rounded-full bg-[#EDE8FD] text-sm font-semibold text-[#613BDB] ring-2 ring-white dark:bg-white/10 dark:text-white dark:ring-bgDark-800"
        >
            {person?.username?.charAt(0).toUpperCase() || "?"}
        </span>
    );
}

function IconButton({ icon, label, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            title={label}
            className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors duration-200 hover:bg-gray-100 hover:text-gray-700 dark:text-grayDark-500 dark:hover:bg-white/10 dark:hover:text-white"
        >
            <SolarIcon name={icon} className="h-[18px] w-[18px]" />
        </button>
    );
}

export default function CheerPopin() {
    const t = useTranslations("cheers");
    const tl = useTranslations("leagues");
    const locale = useLocale();
    const { data: session, status } = useSession();
    const userId = session?.user?.userId;
    const pathname = usePathname() || "";
    const { subscribe } = useMessengerNotifications();

    const [cheers, setCheers] = useState([]);
    // main | snooze | snoozed | returned
    const [mode, setMode] = useState("main");
    const [snoozed, setSnoozed] = useState(null);
    const [busy, setBusy] = useState(false);
    const [hovered, setHovered] = useState(false);
    const seenIdsRef = useRef(new Set());
    // Pause choisie dans cet onglet : on n'affiche plus rien d'ici le
    // prochain chargement, même si un événement arrive encore.
    const mutedRef = useRef(false);

    const add = useCallback((incoming) => {
        if (mutedRef.current) return;
        setCheers((prev) => {
            const known = new Set(prev.map((c) => c.id));
            const fresh = incoming.filter((c) => c?.id && !known.has(c.id));
            return fresh.length ? [...fresh, ...prev] : prev;
        });
    }, []);

    // Encouragements reçus en l'absence de la personne.
    useEffect(() => {
        if (status !== "authenticated" || !userId) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch("/api/cheers/pending", { method: "POST" });
                if (!res.ok) return;
                const data = await res.json();
                if (!cancelled) add(data.cheers || []);
            } catch {
                // Sans réseau, on les relira à la prochaine visite.
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [status, userId, add]);

    // En direct.
    useEffect(() => {
        if (status !== "authenticated" || !userId) return;
        return subscribe("cheerReceived", (cheer) => {
            setMode((current) => (current === "returned" ? "main" : current));
            add([cheer]);
        });
    }, [status, userId, subscribe, add]);

    const quiet = QUIET_PATHS.some((path) => pathname.includes(path));
    const visible = cheers.length > 0 && !quiet;

    // « Vu » seulement quand la bulle est réellement sous les yeux.
    useEffect(() => {
        if (!visible) return;
        const markSeen = () => {
            if (document.hidden) return;
            const ids = cheers
                .map((c) => c.id)
                .filter((id) => !seenIdsRef.current.has(id));
            if (ids.length === 0) return;
            ids.forEach((id) => seenIdsRef.current.add(id));
            fetch("/api/cheers/seen", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ids }),
            }).catch(() => {
                ids.forEach((id) => seenIdsRef.current.delete(id));
            });
        };
        markSeen();
        document.addEventListener("visibilitychange", markSeen);
        return () => document.removeEventListener("visibilitychange", markSeen);
    }, [visible, cheers]);

    const close = useCallback(() => {
        setCheers([]);
        setMode("main");
        setHovered(false);
    }, []);

    // Fermeture seule. Rien ne se ferme pendant qu'on choisit une pause ou
    // qu'on a la souris sur la bulle.
    const closeDelay =
        mode === "main" ? AUTO_CLOSE_MS : mode === "snooze" ? null : 2600;
    const counting = visible && !busy && !hovered && closeDelay !== null;
    useEffect(() => {
        if (!counting) return;
        const id = setTimeout(close, closeDelay);
        return () => clearTimeout(id);
    }, [counting, closeDelay, cheers, close]);

    // Une personne peut avoir encouragé plusieurs jours de suite : un visage.
    const senders = [];
    for (const cheer of cheers) {
        if (!senders.some((s) => s.userId === cheer.from.userId)) {
            senders.push({
                ...cheer.from,
                canReturn: cheer.canReturn,
                isReturn: cheer.isReturn === true,
            });
        }
    }
    const toReturn = senders.filter((s) => s.canReturn).slice(0, 5);

    const cheerBack = async () => {
        if (busy) return;
        setBusy(true);
        try {
            await Promise.all(
                toReturn.map((sender) =>
                    fetch("/api/cheers/send", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ targetId: sender.userId }),
                    }),
                ),
            );
            setMode("returned");
        } catch {
            // Rien à dire : le bouton reste disponible.
        } finally {
            setBusy(false);
        }
    };

    const snooze = async (duration) => {
        if (busy) return;
        setBusy(true);
        try {
            const res = await fetch("/api/cheers/snooze", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ duration }),
            });
            if (res.ok) {
                mutedRef.current = true;
                setSnoozed(duration);
                setMode("snoozed");
            }
        } catch {
            // Le choix reste à l'écran.
        } finally {
            setBusy(false);
        }
    };

    const first = senders[0];
    const firstName = first?.username?.split(" ")[0] || "";
    const single = senders.length === 1;
    const onLeagues = /\/(ligues|leagues)(\/|$)/.test(pathname);

    let meta = t("popin_source");
    if (single && first?.rankLevel) {
        const rank = tl(`${first.rankLevel}`);
        meta =
            first.weeklyXp > 0
                ? t("popin_meta", {
                      rank,
                      xp: first.weeklyXp.toLocaleString(locale),
                  })
                : t("popin_meta_rank", { rank });
    }

    return (
        <AnimatePresence>
            {visible && first && (
                <motion.div
                    role="status"
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                    className="fixed inset-x-3 bottom-24 z-[60] sm:inset-x-auto sm:right-4 sm:w-[380px]"
                >
                    <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl shadow-black/[0.08] dark:border-borderDark/60 dark:bg-bgDark-800 dark:shadow-black/30">
                        {mode === "snooze" ? (
                            <div className="p-4">
                                <div className="flex items-center justify-between gap-2">
                                    <p className="mb-0 text-sm font-semibold text-gray-900 dark:text-white">
                                        {t("snooze_title")}
                                    </p>
                                    <IconButton
                                        icon="close-circle-linear"
                                        label={t("snooze_back")}
                                        onClick={() => setMode("main")}
                                    />
                                </div>
                                <div className="mt-3 grid grid-cols-3 gap-2">
                                    {SNOOZES.map((duration) => (
                                        <button
                                            key={duration}
                                            type="button"
                                            disabled={busy}
                                            onClick={() => snooze(duration)}
                                            className="rounded-xl border border-gray-200 px-2 py-2 text-[13px] font-semibold text-gray-800 transition-colors duration-200 hover:border-gray-900 hover:bg-gray-50 disabled:opacity-60 dark:border-bgDark-300 dark:text-white dark:hover:border-white dark:hover:bg-white/5"
                                        >
                                            {t(`snooze_${duration}`)}
                                        </button>
                                    ))}
                                </div>
                                <Link
                                    href="/account?content=parameters"
                                    onClick={close}
                                    className="mt-3 inline-block text-xs font-medium text-gray-500 underline-offset-2 hover:text-gray-900 hover:underline dark:text-grayDark-400 dark:hover:text-white"
                                >
                                    {t("snooze_forever")}
                                </Link>
                            </div>
                        ) : mode === "snoozed" ? (
                            <div className="flex items-start gap-3 p-4">
                                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-grayDark-300">
                                    <SolarIcon name="bell-off-linear" className="h-[18px] w-[18px]" />
                                </span>
                                <div className="min-w-0">
                                    <p className="mb-0 text-sm font-semibold text-gray-900 dark:text-white">
                                        {t(`snooze_done_${snoozed}`)}
                                    </p>
                                    <p className="mb-0 mt-0.5 text-xs leading-relaxed text-gray-500 dark:text-grayDark-400">
                                        {t("snooze_done_hint")}
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div className="p-4">
                                <div className="flex items-start gap-3">
                                    {single ? (
                                        <span className="relative flex-shrink-0">
                                            <Face person={first} size={44} />
                                            <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-white ring-2 ring-white dark:ring-bgDark-800">
                                                <SolarIcon name="hand-heart-bold" className="h-3 w-3" />
                                            </span>
                                        </span>
                                    ) : (
                                        <span className="flex flex-shrink-0 -space-x-2.5 pt-0.5">
                                            {senders.slice(0, 3).map((sender) => (
                                                <Face key={sender.userId} person={sender} size={36} />
                                            ))}
                                        </span>
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <p className="mb-0 text-[10px] font-bold uppercase tracking-[0.08em] text-primary">
                                            {t("popin_eyebrow")}
                                        </p>
                                        <p className="mb-0 mt-0.5 text-[15px] font-semibold leading-snug text-gray-900 dark:text-white">
                                            {single
                                                ? t(
                                                      first.isReturn
                                                          ? "popin_one_return"
                                                          : "popin_one",
                                                      { name: firstName },
                                                  )
                                                : t("popin_many", {
                                                      name: firstName,
                                                      count: senders.length - 1,
                                                  })}
                                        </p>
                                        <p className="mb-0 mt-1 flex items-center gap-1.5 text-xs text-gray-500 dark:text-grayDark-400">
                                            {single && first.rankLevel && (
                                                <Image
                                                    src={rankIcon(first.rankLevel)}
                                                    alt=""
                                                    width={14}
                                                    height={14}
                                                    className="h-3.5 w-3.5 flex-shrink-0"
                                                />
                                            )}
                                            <span className="truncate">{meta}</span>
                                        </p>
                                    </div>
                                    <div className="-mr-1 -mt-1 flex flex-shrink-0 items-center">
                                        {mode === "main" && (
                                            <IconButton
                                                icon="bell-off-linear"
                                                label={t("snooze_open")}
                                                onClick={() => setMode("snooze")}
                                            />
                                        )}
                                        <IconButton
                                            icon="close-circle-linear"
                                            label={t("popin_close")}
                                            onClick={close}
                                        />
                                    </div>
                                </div>

                                {mode === "returned" ? (
                                    <p className="mb-0 mt-3.5 flex items-center justify-center gap-1.5 rounded-xl bg-emerald-50 py-2.5 text-sm font-semibold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                                        <SolarIcon name="check-circle-bold" className="h-4 w-4" />
                                        {t("popin_returned")}
                                    </p>
                                ) : (
                                    (toReturn.length > 0 || !onLeagues) && (
                                        <div className="mt-3.5 flex items-center gap-2">
                                            {toReturn.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={cheerBack}
                                                    disabled={busy}
                                                    className="btn-3d lip-orange inline-flex min-w-0 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl bg-primary px-3 py-2.5 text-[13px] font-semibold !text-white disabled:opacity-60"
                                                >
                                                    <SolarIcon name="hand-heart-linear" className="h-4 w-4" />
                                                    {t("popin_return")}
                                                </button>
                                            )}
                                            {!onLeagues && (
                                                <Link
                                                    href="/leagues"
                                                    onClick={close}
                                                    className={`inline-flex flex-shrink-0 items-center justify-center whitespace-nowrap rounded-xl border border-gray-200 px-3 py-2.5 text-[13px] font-semibold !text-gray-700 transition-colors duration-200 hover:border-gray-300 hover:bg-gray-50 dark:border-bgDark-300 dark:!text-grayDark-300 dark:hover:bg-white/5 ${
                                                        toReturn.length > 0 ? "" : "flex-1"
                                                    }`}
                                                >
                                                    {t("popin_see_ranking")}
                                                </Link>
                                            )}
                                        </div>
                                    )
                                )}
                            </div>
                        )}

                        {/* Temps restant avant la fermeture. */}
                        {mode === "main" && counting && (
                            <motion.span
                                key={cheers.length}
                                aria-hidden="true"
                                initial={{ scaleX: 1 }}
                                animate={{ scaleX: 0 }}
                                transition={{ duration: AUTO_CLOSE_MS / 1000, ease: "linear" }}
                                className="absolute inset-x-0 bottom-0 block h-[3px] origin-left bg-primary/25"
                            />
                        )}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
